import { Input } from '~/components/common/CurieInput'
import { Button } from '~/components/common/Button'
import { useDisclosure } from '@nextui-org/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { FC, FormEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import { SiteLayout } from '~/components/layout/SiteLayout'
import { QueryKeys } from '~/services/queryClient'
import { updateUserProfile, completeOnboarding } from '~/services/userService'
import { BetterToast } from '~/utils/toast'
import { useUserContext } from '~/contexts/UserContext'

import 'swiper/css'

export const Page: FC = () => {
  const [name, setName] = useState('')
  const [activateTab, setActivateTab] = useState(0)
  const [totalPage, setTotalPage] = useState(1)
  const [isDisabled, setIsDisabled] = useState(false)
  const { refetch } = useUserContext()
  const [swiper, setSwiper] = useState<SwiperType | null>(null)
  const queryClient = useQueryClient()
  const { mutate: updateUser } = useMutation({
    mutationFn: updateUserProfile,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: QueryKeys.User })
    }
  })

  const { onOpenChange: onAuthBranchModalOpenChange } = useDisclosure()
  const navigate = useNavigate()

  useEffect(() => {
    const INTEGRATIONS_TAB_INDEX = 1
    if (swiper) {
      const url = new URL(window.location.href)
      const query = url.searchParams
      const tab = query.get('tab')
      // NOTE: msg query parameter handling removed - now using Redux flash messages

      if (query.get('key')) {
        setTotalPage(INTEGRATIONS_TAB_INDEX)
      } else if (tab) {
        query.delete('tab')

        if (tab) {
          swiper.slideTo(INTEGRATIONS_TAB_INDEX)
          setActivateTab(parseInt(tab))
          query.delete('conn')
          onAuthBranchModalOpenChange()
        }
        window.history.replaceState({}, '', url.toString())
      }
    }
  }, [swiper, onAuthBranchModalOpenChange])

  const handleUpdateName = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!name) {
      return BetterToast.error('Your name cannot be empty')
    }

    setIsDisabled(true)
    const onError = () => {
      BetterToast.error('An error occurred. Please try again later')
      setIsDisabled(false)
    }
    updateUser({ name }, { onSuccess: goToNextPage, onError })
  }

  const goToNextPage = () => {
    if (activateTab + 1 === totalPage) {
      return handleFinish()
    }
    swiper!.slideTo(activateTab + 1)
    setActivateTab(activateTab + 1)
  }

  const handleFinish = async () => {
    try {
      // Mark user as onboarded
      await completeOnboarding()

      // Refresh user context to get updated onboarding status
      await refetch()

      // Let the centralized verification flow handle phone verification and other steps
      const query = new URLSearchParams(window.location.search)
      const returnUrl = query.get('return_url')

      if (returnUrl) {
        // Decode and redirect to the original destination
        const decodedReturnUrl = decodeURIComponent(returnUrl)
        navigate(decodedReturnUrl)
        return
      }

      navigate('/')
    } catch (error) {
      console.error('Error completing onboarding:', error)
      BetterToast.error('Failed to complete onboarding. Please try again.')
    }
  }

  const swiperRef = useRef<SwiperType | null>(null)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        let swiperInstance: any = swiperRef.current
        if (!swiperInstance) return
        swiperInstance = swiperInstance.swiper

        const activeSlide = swiperInstance.slides[swiperInstance.activeIndex]
        const focusableElements = activeSlide.querySelectorAll(
          'a, button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
        ) as NodeListOf<HTMLElement>
        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus()
            event.preventDefault()
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus()
            event.preventDefault()
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return (
    <SiteLayout>
      <div className='max-w-2xl mx-auto h-full'>
        <Swiper
          className='swiper w-full h-full px-4'
          allowTouchMove={false}
          noSwiping={true}
          slidesPerView={1}
          ref={swiperRef}
          onSwiper={setSwiper as (swiper: any) => void}
        >
          <SwiperSlide
            className='w-full flex-col justify-center items-center'
            style={{ display: 'flex' }}
          >
            <div className='w-full max-w-lg p-5'>
              <h1
                className='mb-5 [20px] text-center text-[rgba(0,0,0,0.75)] font-semibold text-2xl'
                aria-label='name-input-title'
              >
                What is your name?
              </h1>
              <div className='mb-3 w-full text-center text-[rgba(0,0,0,0.5)]'>
                This is how you&apos;ll appear on Curie. Your name can be changed later.
              </div>
              <form id='setNameForm' className='mt-10' onSubmit={handleUpdateName}>
                <div>
                  <Input
                    type='text'
                    id='name'
                    placeholder='Enter your name'
                    value={name}
                    onChange={e => setName(e.target.value)}
                    aria-label='name-input'
                    autoComplete='username'
                  />
                </div>
                <div className='flex mt-[50px]'>
                  <Button variant='outlined' className='w-1/3 m-0' onClick={goToNextPage}>
                    Skip
                  </Button>
                  <div className='w-1/3 text-center flex items-center justify-center text-gray-500'>
                    1/{totalPage}
                  </div>
                  <Button
                    variant='primary'
                    type='submit'
                    aria-label='name-confirm-button'
                    isDisabled={isDisabled}
                    className='w-1/3 m-0'
                  >
                    Confirm
                  </Button>
                </div>
              </form>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </SiteLayout>
  )
}

export default Page
